import React, { useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import KindredLogo from '../../Common/Logos/KindredLogo';
import { themeColor } from '../../Theming';
import images from '../../Theming/images';
import { Trans, useTranslation } from 'react-i18next';
import usePageTitle from '../../Common/Hooks/usePageTitle';
import { useFeatures } from '../../PluginFeatures';
import {
  getRandomUserId,
  getUtmParamsForChromeExtension,
} from '../../Common/Utils/extensionSDK';
import { sendExtensionInstalled } from '../../Extension/ExtensionInstalled';
import {
  getBrowserCountry,
  getLocationCurrency,
} from '../../Common/Utils/locations';
import env from '../../env';
import { sendPartnerFeatures } from '../../PluginFeatures/helpers';
import { usePageViewTracking, useQuery } from '../../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackClickedActionEvent,
} from '../../Common/Analytics/utils';

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: white;
`;

const MainContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  clip-path: circle(88vh at 50% -28vh);
  background-color: #f5f5f5;
  padding-top: 10%;

  & > p {
    margin: 0;
    text-align: center;
    font-size: 35px;
    font-weight: 700;
    color: ${themeColor('darkBlue')};
  }
`;

const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const BrandListImage = styled.img`
  width: 100%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 999;
`;

const KindredLogoContainer = styled.div`
  transform: scale(1.5);
  margin-bottom: 44px;
`;

const BottomContent = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 50px 0 60px 0;
  height: 40%;
  z-index: 999;

  & > div {
    & > h3 {
      margin: 0;
      padding: 0;
      text-align: center;
      font-size: 18px;
      font-weight: 700;
      color: ${themeColor('darkBlue')};
    }
  }

  & > p {
    margin: 0;
    padding: 0;
    max-width: 457px;
    text-align: center;
    font-size: 14px;
    color: ${themeColor('darkBlue')};
  }

  & a {
    text-decoration: underline;
    cursor: pointer;
    color: ${themeColor('darkBlue')};
  }
`;

const Button = styled.button`
  background-color: #52ae67;
  color: white;
  border: none;
  padding: 1.2rem 3rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 336px;
  height: 61px;
  margin-top: 50px;
`;

const DesktopExtensionLanding: React.FC = () => {
  const query = useQuery();
  const utm_campaign = query.get('utm_campaign') as PartnerNames;
  const partnerName = utm_campaign ?? 'Kindred';
  usePageViewTracking(
    ANALYTICS_PAGE_NAMES.chrome_desktop_success_page,
    partnerName
  );
  const theme = useTheme();
  const { t } = useTranslation();
  usePageTitle('Extension Installed');
  const { autoActivation, loading } = useFeatures();

  useEffect(() => {
    const utmParams = getUtmParamsForChromeExtension();
    sendExtensionInstalled(utmParams);

    const browserCountry = getBrowserCountry();
    const randomUserId = getRandomUserId();

    window.setKindredUser(env.kindred_extension_id, {
      id: randomUserId,
      currency: getLocationCurrency(browserCountry),
      countryCode: browserCountry,
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      sendPartnerFeatures(autoActivation);
    }
  }, [loading, autoActivation]);

  const onCTAClicked = () => {
    trackClickedActionEvent(
      query,
      partnerName,
      ANALYTICS_ACTION_LABELS.checkout_deals,
      ANALYTICS_PAGE_NAMES.chrome_desktop_success_page
    );
    window.location.href = 'https://commerce.kindred.co/discounts';
  };

  return (
    <Container id="mainContainer">
      <MainContent>
        <HyperisePlaceholderContainer id="beforeLogo">
          <p></p>
        </HyperisePlaceholderContainer>
        <KindredLogoContainer>
          <KindredLogo color={theme.colors.darkBlue} />
        </KindredLogoContainer>
        <HyperisePlaceholderContainer id="afterLogo">
          <p></p>
        </HyperisePlaceholderContainer>

        <p id="allDoneText">
          {t('extension.desktopActivation.allDone')}
          <span role="img" aria-label="party popper">
            {' '}
            🎉
          </span>
          <br />
          {t('extension.desktopActivation.seeYouAtCheckout')}
        </p>
      </MainContent>
      <BrandListImage
        id="brandListImage"
        src={images.brands.default}
        alt="Brands"
      />
      <BottomContent>
        <div>
          <Button id="checkOutDealsButton" onClick={onCTAClicked}>
            {t('extension.desktopActivation.checkOutDeals')}
          </Button>
        </div>
        <p id="questionsText">
          <Trans i18nKey="extension.desktopActivation.questions">
            <a
              id="faqLink"
              href={'https://sdk.kindred.co/FAQ'}
              target="_blank"
              rel="noopener noreferrer"
            >
              FAQ
            </a>
          </Trans>
        </p>
      </BottomContent>
    </Container>
  );
};

export default DesktopExtensionLanding;
